import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'error';

/** One write. The signal aborts it once it has taken too long (WRITE_TIMEOUT_MS, more on a retry): pass it on to fetch. */
type Run = (signal: AbortSignal) => Promise<unknown>;

interface Entry {
  key: string;
  run: Run;
  /** The debounce of a text still being typed; the entry goes at once when another write queues behind it. */
  timer: ReturnType<typeof setTimeout> | null;
  sending: boolean;
  /** An attempt did not get through; the footer says so until one does. */
  failed: boolean;
  tries: number;
}

/** Wait before each retry; the last value repeats until the write goes through. */
export const RETRY_MS = [800, 2000, 5000, 10000, 15000];
/**
 * A write still unanswered after this long (a stalled connection) is aborted and retried; a retry gets
 * twice and then three times as long, so a link that is merely slow still gets its answer through. flush()
 * waits this long at most: the write carries on behind it.
 */
export const WRITE_TIMEOUT_MS = 10000;

const errorStatus = (err: unknown) => (err as { status?: number } | null)?.status ?? 0;
const errorCode = (err: unknown) => (err as { code?: string } | null)?.code;
/** 4xx other than 408/429 will not get better by retrying; a 409 conflict (two saves of one answer raced) will. */
export const permanent = (err: unknown) => {
  const status = errorStatus(err);
  return status >= 400 && status < 500 && status !== 408 && status !== 429 && errorCode(err) !== 'conflict';
};

function drop(queue: Entry[], entry: Entry) {
  const i = queue.indexOf(entry);
  if (i >= 0) queue.splice(i, 1);
}

function attempt(run: Run, timeoutMs: number): Promise<unknown> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const err = Object.assign(new Error('The write took too long.'), { name: 'TimeoutError' });
      controller.abort(err);
      reject(err);
    }, timeoutMs);
  });
  return Promise.race([Promise.resolve().then(() => run(controller.signal)), timeout]).finally(() => clearTimeout(timer));
}

/**
 * Autosave on every screen: choices go out immediately, text is debounced. Every write goes through one
 * queue, one at a time, in the order the respondent made them: the server checks an answer against the
 * answers it already holds (C2 offers only C1's picks and drops any other, E3 shows only after E2), so a
 * C2 that overtook the C1 it was picked from would be stored empty with a 200 and nobody would know.
 * Saving a key again replaces its value while it waits at the end of the queue, so taps coalesce.
 *
 * A write that fails holds the queue and is retried with backoff, at once when the browser is back
 * online, and when `flush()` asks, until it goes through: nothing is dropped because the network was down
 * for a while, and nothing behind it lands first. `flush()` resolves with the keys still unsaved once
 * every write queued before it landed or one failed, so /complete and /finish do not go ahead without
 * them, nor wait on a stalled connection for more than WRITE_TIMEOUT_MS. A write the server refused for good (4xx)
 * is dropped; `onRejected` hears of it so the respondent can be told, rather than only the console.
 */
export function useAutosave(opts: { onRejected?: (key: string, err: unknown) => void } = {}) {
  const onRejected = useRef(opts.onRejected);
  onRejected.current = opts.onRejected;
  const queue = useRef<Entry[]>([]);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoff = useRef(0);
  // flush() callers, each a check that resolves its promise and returns true once it is satisfied.
  const waiters = useRef<(() => boolean)[]>([]);
  // While a flush waits, a key saved again is not debounced: it would hold the flush back.
  const flushing = useRef(0);
  const [status, setStatus] = useState<SaveStatus>('idle');

  const settle = useCallback(() => {
    const head = queue.current[0];
    setStatus(!head ? 'idle' : head.failed ? 'error' : 'saving');
    waiters.current = waiters.current.filter((check) => !check());
  }, []);

  // `pump` and the retry timer call each other; the pump lives in a ref.
  const pump = useRef<() => void>(() => undefined);

  const scheduleRetry = useCallback(() => {
    if (retryTimer.current) return;
    const delay = RETRY_MS[Math.min(backoff.current, RETRY_MS.length - 1)]!;
    backoff.current += 1;
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      pump.current();
    }, delay);
  }, []);

  /** Sends the failed write now rather than after its backoff. */
  const retryNow = useCallback(() => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
    pump.current();
  }, []);

  /** A debounce timer; whichever entry holds it when it fires is due. */
  const arm = useCallback((ms: number) => {
    const id = setTimeout(() => {
      for (const entry of queue.current) if (entry.timer === id) entry.timer = null;
      pump.current();
    }, ms);
    return id;
  }, []);

  pump.current = () => {
    const q = queue.current;
    // A newer value of the same key right behind the head replaces its value: nothing comes between them.
    // The head entry stays (a flush may be waiting for it).
    while (q.length > 1 && !q[0]!.sending && q[1]!.key === q[0]!.key) {
      const head = q[0]!;
      const [newer] = q.splice(1, 1);
      if (head.timer) clearTimeout(head.timer);
      head.run = newer!.run;
      head.timer = newer!.timer;
    }
    const head = q[0];
    if (!head || head.sending || (head.failed && retryTimer.current)) return;
    if (head.timer) {
      // Still being typed, and nothing waits behind it: its debounce sends it.
      if (q.length === 1) return;
      clearTimeout(head.timer);
      head.timer = null;
    }
    head.sending = true;
    const timeoutMs = WRITE_TIMEOUT_MS * Math.min(1 + head.tries, 3);
    head.tries += 1;
    attempt(head.run, timeoutMs).then(
      () => {
        drop(q, head);
        backoff.current = 0;
        settle();
        pump.current();
      },
      (err: unknown) => {
        head.sending = false;
        if (permanent(err)) {
          drop(q, head);
          console.warn('[autosave] rejected', head.key, err);
          // A newer value queued for this key may still go through.
          if (!q.some((e) => e.key === head.key)) onRejected.current?.(head.key, err);
          settle();
          pump.current();
          return;
        }
        head.failed = true;
        scheduleRetry();
        settle();
      },
    );
    settle();
  };

  const save = useCallback(
    (key: string, run: Run, debounceMs = 0) => {
      const q = queue.current;
      const tail = q[q.length - 1];
      let entry: Entry;
      if (tail && tail.key === key && !tail.sending) {
        // The newest value replaces the one waiting at the end (a failed write is retried with it).
        if (tail.timer) clearTimeout(tail.timer);
        tail.run = run;
        tail.timer = null;
        entry = tail;
      } else {
        entry = { key, run, timer: null, sending: false, failed: false, tries: 0 };
        q.push(entry);
      }
      if (debounceMs > 0 && flushing.current === 0) entry.timer = arm(debounceMs);
      pump.current();
      settle();
    },
    [arm, settle],
  );

  /**
   * Sends what is still debounced and a failed write without waiting for its backoff, then waits until
   * every write queued so far landed, or one failed (the rest is behind it), for WRITE_TIMEOUT_MS at most;
   * resolves with the keys unsaved.
   */
  const flush = useCallback((): Promise<string[]> => {
    const mine = new Set(queue.current);
    for (const entry of mine) {
      if (entry.timer) clearTimeout(entry.timer);
      entry.timer = null;
    }
    flushing.current += 1;
    retryNow();
    return new Promise<string[]>((resolve) => {
      let deadline: ReturnType<typeof setTimeout> | undefined;
      const finish = () => {
        clearTimeout(deadline);
        flushing.current -= 1;
        resolve([...new Set(queue.current.map((e) => e.key))]);
        return true;
      };
      const check = () => {
        const q = queue.current;
        const head = q[0];
        if (q.some((e) => mine.has(e)) && !(head && head.failed && !head.sending)) return false;
        return finish();
      };
      if (check()) return;
      waiters.current.push(check);
      deadline = setTimeout(() => {
        waiters.current = waiters.current.filter((w) => w !== check);
        finish();
      }, WRITE_TIMEOUT_MS);
    });
  }, [retryNow]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush();
    };
    const onOnline = () => retryNow();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('online', onOnline);
    };
  }, [flush, retryNow]);

  useEffect(
    () => () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    [],
  );

  return { save, flush, status };
}
